import { useMutation } from "@tanstack/react-query";

import { changePassword } from "api/landlord/auth";
import { FC } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  Modal
} from "shared/components";
import { useForm } from "shared/hooks";
import { getErrorMessage } from "utils/error";
import { object, ref, string } from "yup";
import { toast } from "react-toastify";

type Props = {
  opened: boolean;
  onClose: () => void;
};

const validationSchema = object().shape({
  oldPassword: string().required(),
  newPassword: string().min(8).required(),
  confirmPassword: string()
    .min(8)
    .required()
    .oneOf([ref("newPassword"), ""], "Passwords must match")
});

export const ChangePasswordModal: FC<Props> = ({ opened, onClose }) => {
  const { register, onSubmit, values } = useForm(
    {
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    },
    validationSchema
  );

  const { mutate } = useMutation({
    mutationFn: () => {
      return changePassword(values.oldPassword, values.newPassword);
    },
    onSuccess: () => {
      onClose();
      toast.success("Password successfully changed");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} panelClassName="w-[366px] p-6">
      <header className="mb-4 text-center text-xl font-bold text-content-1000">
        Change password
      </header>
      <form
        onSubmit={onSubmit({
          onSuccess: () => mutate()
        })}
      >
        <div className="mb-4">
          <Input
            id="old_password"
            label="Old Password"
            placeholder="·············"
            type="password"
            required={true}
            {...register("oldPassword")}
          />
        </div>
        <div className="mb-4">
          <Input
            id="new_password"
            label="New Password"
            type="password"
            placeholder="·············"
            required={true}
            {...register("newPassword")}
          />
        </div>
        <Input
          id="confirm_password"
          label="Confirm Password"
          type="password"
          placeholder="·············"
          required={true}
          {...register("confirmPassword")}
        />
        <div className="mt-10 flex gap-2">
          <div className="w-full">
            <Button size={ButtonSize.BASE} onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
          <div className="w-full">
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              type="submit"
              className="w-full"
            >
              Change
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
